import { React, useEffect, useState } from "react";
import PageTitle from "../../PageTitle";
import useAxios from "../../../utilities/useAxios";
import { getCurrentUserProfile } from "../../../utilities/AuthService";
import {
	Form,
	Input,
	DatePicker,
	TimePicker,
	Select,
	Button,
	message,
	Card,
	ConfigProvider,
	Typography,
} from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import { useNavigate, useLocation } from "react-router-dom";
import { getRoomDisplayLabel } from "./rentSanctuaryUtils";
import useIsMobile from "../../../hooks/useIsMobile";

dayjs.locale("ko");

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const SANCTUARY_OTHER_VALUE = "__OTHER__";

const ApplyRent = () => {
	const isMobile = useIsMobile();
	const [form] = Form.useForm();
	const sanctuaryRoomChoice = Form.useWatch("sanctuary_room", form);
	const [loading, setLoading] = useState(false);
	const [sanctuaryRoomList, setSanctuaryRoomList] = useState([]);
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		getSanctuaryRoomList();
		fetchUserProfile();
		const searchParams = new URLSearchParams(location.search);
		const date = searchParams.get("date");
		if (date) {
			form.setFieldsValue({
				date: dayjs(date),
				startTime: dayjs(date).hour(9).minute(0),
				endTime: dayjs(date).hour(10).minute(0),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetchUserProfile, form are stable
	}, [location]);

	const fetchUserProfile = async () => {
		try {
			const profile = await getCurrentUserProfile();
			if (profile) {
				form.setFieldsValue({
					representative: profile.name || "",
					representative_tel: profile.phone_number || "",
				});
			}
		} catch (error) {
			console.log("사용자 프로필을 불러올 수 없습니다:", error);
		}
	};

	const getSanctuaryRoomList = async () => {
		try {
			const response = await useAxios.get("/utils/sanctuary/list/");
			if (response.status === 200) {
				const list = [...response.data].sort((a, b) => {
					if (a.sanctuary_name === b.sanctuary_name) {
						return a.room_name.localeCompare(b.room_name);
					}
					return a.sanctuary_name.localeCompare(b.sanctuary_name);
				});
				setSanctuaryRoomList(list);
			}
		} catch (error) {
			message.error("성전 방 목록을 불러오는데 실패했습니다.");
			console.log(error);
		}
	};

	const onFinish = async (values) => {
		setLoading(true);
		try {
			if (values.endTime <= values.startTime) {
				message.error("종료 시간은 시작 시간보다 크게 설정해주세요.");
				return;
			}

			const startDateTime = dayjs(values.date)
				.hour(values.startTime.hour())
				.minute(values.startTime.minute())
				.second(0)
				.millisecond(0);

			const endDateTime = dayjs(values.date)
				.hour(values.endTime.hour())
				.minute(values.endTime.minute())
				.second(0)
				.millisecond(0);

			let sanctuary_room = values.sanctuary_room;
			let custom_sanctuary_name = "";
			if (sanctuary_room === SANCTUARY_OTHER_VALUE) {
				sanctuary_room = null;
				custom_sanctuary_name = (values.custom_sanctuary_name || "").trim();
			}

			const formData = {
				sanctuary_room,
				custom_sanctuary_name,
				start_datetime: startDateTime.format("YYYY-MM-DD HH:mm:ss"),
				end_datetime: endDateTime.format("YYYY-MM-DD HH:mm:ss"),
				rent_team: values.rent_team,
				rent_for: values.rent_for,
				representative: values.representative,
				representative_tel: values.representative_tel,
			};

			const response = await useAxios.post(
				"/utils/rental/sanctuary/apply/",
				formData,
			);

			if (response.status === 201) {
				message.success("성전 대여 신청이 완료되었습니다.");
				form.resetFields();
				navigate(
					"/utils/rent-sanctuary/view-rental-sanctuary-schedule",
				);
			} else {
				message.error(
					response.data.error || "신청 처리 중 오류가 발생했습니다.",
				);
			}
		} catch (error) {
			if (error.response && error.response.data.error) {
				message.error(error.response.data.error);
			} else {
				message.error("신청 처리 중 오류가 발생했습니다.");
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="성전 대여 신청" />
			<div className="utils-rent_sanctuary__container">
				<div className="utils-rent_sanctuary__header">
					<h1 className="utils-rent_sanctuary__header__title">
						성전 대여 신청
					</h1>
				</div>
				<div className="utils-rent_sanctuary__content-container">
					<Card className="utils-rent_sanctuary__form-card">
						<Form
							form={form}
							layout="vertical"
							onFinish={onFinish}
							initialValues={{
								date: dayjs().startOf("day"),
								startTime: dayjs()
									.startOf("day")
									.hour(9)
									.minute(0),
								endTime: dayjs()
									.startOf("day")
									.hour(10)
									.minute(0),
							}}>
							<Form.Item
								name="sanctuary_room"
								label="성전 선택"
								rules={[
									{
										required: true,
										message: "성전을 선택해주세요",
									},
								]}>
								<Select placeholder="성전을 선택하세요">
									{sanctuaryRoomList.map((sanctuaryRoom) => (
										<Option
											key={sanctuaryRoom.id}
											value={sanctuaryRoom.id}>
											{getRoomDisplayLabel(sanctuaryRoom)}
										</Option>
									))}
									<Option
										key="__OTHER__"
										value={SANCTUARY_OTHER_VALUE}>
										기타
									</Option>
								</Select>
							</Form.Item>

							{sanctuaryRoomChoice === SANCTUARY_OTHER_VALUE && (
								<Form.Item
									name="custom_sanctuary_name"
									label="성전 이름"
									rules={[
										{
											required: true,
											message: "성전 이름을 입력해주세요",
										},
									]}>
									<Input placeholder="신청할 성전 이름을 입력하세요" />
								</Form.Item>
							)}

							<Form.Item
								name="date"
								label="날짜"
								rules={[
									{
										required: true,
										message: "날짜를 선택해주세요",
									},
								]}>
								<DatePicker
									inputReadOnly={isMobile}
									style={{ width: "100%" }}
									format="YYYY-MM-DD"
									allowClear={false}
								/>
							</Form.Item>

							<Form.Item
								name="startTime"
								label="시작 시간"
								rules={[
									{
										required: true,
										message: "시작 시간을 선택해주세요",
									},
									{
										validator(_, value) {
											if (!value) return Promise.resolve();
											const m = value.minute();
											if (m !== 0 && m !== 30) {
												return Promise.reject(
													new Error(
														"분은 00분 또는 30분만 선택할 수 있습니다.",
													),
												);
											}
											return Promise.resolve();
										},
									},
								]}>
								<TimePicker
									inputReadOnly={isMobile}
									style={{ width: "100%" }}
									format="HH:mm"
									minuteStep={30}
									allowClear={false}
								/>
							</Form.Item>

							<Form.Item
								name="endTime"
								label="종료 시간"
								rules={[
									{
										required: true,
										message: "종료 시간을 선택해주세요",
									},
									{
										validator(_, value) {
											if (!value) return Promise.resolve();
											const m = value.minute();
											if (m !== 0 && m !== 30) {
												return Promise.reject(
													new Error(
														"분은 00분 또는 30분만 선택할 수 있습니다.",
													),
												);
											}
											return Promise.resolve();
										},
									},
								]}>
								<TimePicker
									inputReadOnly={isMobile}
									style={{ width: "100%" }}
									format="HH:mm"
									minuteStep={30}
									allowClear={false}
								/>
							</Form.Item>

							<Form.Item
								name="rent_team"
								label="교구/기관명"
								rules={[
									{
										required: true,
										message: "교구/기관명을 입력해주세요",
									},
								]}>
								<Input placeholder="교구/기관명을 입력하세요" />
							</Form.Item>

							<Form.Item
								name="rent_for"
								label="사용 목적"
								rules={[
									{
										required: true,
										message: "사용 목적을 입력해주세요",
									},
								]}>
								<TextArea
									rows={4}
									placeholder="사용 목적을 입력하세요"
								/>
							</Form.Item>

							<Form.Item
								name="representative"
								label="신청자 이름"
								rules={[
									{
										required: true,
										message: "신청자 이름을 입력해주세요",
									},
								]}>
								<Input placeholder="신청자 이름을 입력하세요" />
							</Form.Item>

							<Form.Item
								name="representative_tel"
								label="신청자 연락처"
								rules={[
									{
										required: true,
										message: "신청자 연락처를 입력해주세요",
									},
									{
										pattern: /^[0-9-]+$/,
										message: "숫자와 하이픈(-)만 입력 가능합니다",
									},
								]}>
								<Input placeholder="연락처를 입력하세요 (예: 010-1234-5678)" />
							</Form.Item>

							<Form.Item name="warning_text">
								<Text type="danger">
									아브라함성전 전체가 예약되어 있으면 개별 강의실
									대여 및 신청이 불가능합니다.
									<br />
									또한 아브라함성전 강의실이 예약되어 있으면 성전
									전체 대여 및 신청이 불가능합니다.
								</Text>
							</Form.Item>

							<Form.Item>
								<Button
									type="primary"
									htmlType="submit"
									loading={loading}
									block>
									신청하기
								</Button>
							</Form.Item>
						</Form>
					</Card>
				</div>
			</div>
		</ConfigProvider>
	);
};

export default ApplyRent;
